import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { PageHeader } from '@/components/shared/page-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOrgSettings } from '@/lib/org';
import { MessagesTab } from './editor/messages-tab';
import { PageEditor } from './editor/page-editor';
import { PagesPanel } from './editor/pages-panel';
import { ReviewsTab } from './editor/reviews-tab';
import { ThemeTab } from './editor/theme-tab';
import { usePages } from './editor/types';

const TABS = ['paginas', 'tema', 'mensagens', 'avaliacoes'] as const;

export function SiteEditorPage() {
  const [tab, setTab] = useQueryState('aba', parseAsStringLiteral(TABS).withDefault('paginas'));
  const [pageParam, setPageParam] = useQueryState('pagina', parseAsString);
  const pages = usePages();
  const org = useOrgSettings();
  const dirty = useRef(false);
  const [, force] = useState(0);
  const onDirty = useCallback((d: boolean) => {
    dirty.current = d;
    force((n) => n + 1);
  }, []);

  useEffect(() => {
    document.title = 'Site · BarbeiroPro';
  }, []);

  const list = pages.data ?? [];
  const selected = list.find((p) => p.id === pageParam) ?? list.find((p) => p.slug === '') ?? list[0];

  function selectPage(id: string) {
    if (dirty.current && id !== selected?.id && !window.confirm('Há alterações não salvas nesta página. Descartar e trocar de página?')) return;
    void setPageParam(id);
  }

  return (
    <div>
      <PageHeader title="Site" description="Monte as páginas, escolha o visual e publique. O agendamento online já vem incluso." />
      <Tabs
        value={tab}
        onValueChange={(v) => {
          if (dirty.current && v !== 'paginas' && !window.confirm('Há alterações não salvas nesta página. Descartar?')) return;
          void setTab(v as (typeof TABS)[number]);
        }}
      >
        <TabsList>
          <TabsTrigger value="paginas">Páginas</TabsTrigger>
          <TabsTrigger value="tema">Tema</TabsTrigger>
          <TabsTrigger value="mensagens">Mensagens</TabsTrigger>
          <TabsTrigger value="avaliacoes">Avaliações</TabsTrigger>
        </TabsList>
        <TabsContent value="paginas" className="mt-3">
          {pages.isPending && <LoadingState />}
          {pages.isError && <ErrorState error={pages.error} onRetry={() => pages.refetch()} />}
          {pages.data && !selected && <EmptyState title="Nenhuma página" description="Crie a primeira página do site." />}
          {pages.data && selected && (
            <div className="grid items-start gap-5 lg:grid-cols-[15rem_minmax(0,1fr)]">
              <PagesPanel pages={list} selectedId={selected.id} onSelect={selectPage} />
              <PageEditor key={selected.id} pageId={selected.id} pages={list} orgSlug={org.data?.slug} onDirtyChange={onDirty} />
            </div>
          )}
        </TabsContent>
        <TabsContent value="tema" className="mt-3"><ThemeTab /></TabsContent>
        <TabsContent value="mensagens" className="mt-3"><MessagesTab /></TabsContent>
        <TabsContent value="avaliacoes" className="mt-3"><ReviewsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
